<template>
    <div>

        <div v-if="loading" class="text-center">
            <i class="fa fa-circle-o-notch fa-spin"></i> Loading
        </div>
        <div v-else>
            <vue-headful :title="incidentName+ ' :: MeritEHS'"/>

            <h4 class="py-3 mb-4">
                <ol class="breadcrumb font-weight-bold m-0 d-block d-md-inline-block">
                    <li class="breadcrumb-item font-weight-light d-block d-md-inline-block">
                        <router-link :to="`/incidents`">
                            <i class="fa fa-stethoscope"></i> Incidents
                        </router-link>
                    </li>
                    <li class="breadcrumb-item active d-block d-md-inline-block mt-md-0 mt-2">
                        <incident-name :incident="incident" />
                    </li>
                </ol>
            </h4>

            <div class="row">
                <div class="col-xl-10 col-lg-12">

                    <div class="card mb-3">
                        <div class="card-header">
                            <strong>Incident Details</strong>
                            <div class="float-sm-right float-none d-print-none d-block d-sm-inline-block mt-2 mt-sm-0">
                                <router-link :to="`/incidents/edit/${incident.IncidentId}`" class="btn btn-sm btn-light">
                                    <i class="fa fa-edit"></i> Edit
                                </router-link>
                                <a :href="emailLink" class="btn btn-sm btn-light">
                                    <i class="fa fa-envelope"></i> Email
                                </a>
                                <router-link :to="`/incidents/pdf/${IncidentId}`" class="btn btn-sm btn-light">
                                    <i class="fa fa-file-pdf-o"></i> PDF
                                </router-link>
                                <router-link :to="`/incidents/delete/${incident.IncidentId}`" class="btn btn-sm btn-light">
                                    <i class="fa fa-trash"></i> Delete
                                </router-link>
                            </div>
                        </div>
                        <div class="card-body">
                            <div class="row">
                                <div class="col-lg-6">
                                    <div class="row mb-2">
                                        <div class="col text-muted">Status</div>
                                        <div class="col-md-8"><h4 :class="`badge badge-${incident.status_color}`">{{ incident.status_name }}</h4></div>
                                    </div>
                                    <div class="row mb-2">
                                        <div class="col text-muted">Incident Date</div>
                                        <div class="col-md-8">{{ incident.IncidentDate | moment("MM/DD/YYYY") }}</div>
                                    </div>
                                    <div class="row mb-2">
                                        <div class="col text-muted">Type of Claim</div>
                                        <div class="col-md-8">{{ incident.IncidentTypeName }}</div>
                                    </div>
                                    <div class="row mb-2">
                                        <div class="col text-muted">Employee</div>
                                        <div class="col-md-8">
                                            <router-link :to="`/employees/view/${incident.EmployeeId}`" v-if="incident.EmployeeId">
                                                {{ incident.EmpFirstName }} {{ incident.EmpLastName }}
                                            </router-link>
                                            <span v-else>{{ incident.TempEmployeeName }}</span>
                                        </div>
                                    </div>
                                    <div class="row mb-2">
                                        <div class="col text-muted">Exact Location of Incident</div>
                                        <div class="col-md-8">{{ incident.IncidentLocation }}</div>
                                    </div>
                                    <div class="row mb-2">
                                        <div class="col text-muted">Nature of Injury:</div>
                                        <div class="col-md-8">{{ incident.InjuryTypeName }}</div>
                                    </div>
                                </div>
                                <div class="col-lg-6">
                                    <div class="row mb-2">
                                        <div class="col text-muted">Is Incident Time Undetermined?</div>
                                        <div class="col-md-5">
                                            <span v-if="incident.IsIncidentTimeUndetermined">Yes</span>
                                            <span v-else>No</span>
                                        </div>
                                    </div>
                                    <div class="row mb-2">
                                        <div class="col text-muted">Time of Incident</div>
                                        <div class="col-md-5">{{ incident.IncidentTime | time }}</div>
                                    </div>
                                    <div class="row mb-2">
                                        <div class="col text-muted">Date Employee Reported Incident to Supervisor</div>
                                        <div class="col-md-5">{{ incident.IncidentReportedDate | moment("MM/DD/YYYY") }}</div>
                                    </div>
                                    <div class="row mb-2">
                                        <div class="col text-muted">Time Work Began</div>
                                        <div class="col-md-5">{{ incident.BeginWorkTime | time }}</div>
                                    </div>
                                    <div class="row mb-2">
                                        <div class="col text-muted">Did Incident Occur Due to Rework?</div>
                                        <div class="col-md-5">
                                            <label class="badge badge-success" v-if="incident.due_to_rework">Yes</label>
                                            <label class="badge badge-danger" v-else>No</label>
                                        </div>
                                    </div>
                                    <div class="row mb-2">
                                        <div class="col text-muted">Major Body Part:</div>
                                        <div class="col-md-5">{{ incident.MajorBodyPartName }}</div>
                                    </div>
                                    <div class="row mb-2">
                                        <div class="col text-muted">Specific Body Part:</div>
                                        <div class="col-md-5">{{ incident.SpecificBodyPartName }}</div>
                                    </div>
                                </div>
                            </div>
                            <hr/>

                            <div class="mb-3">
                                <span class="text-muted">Incident Witness:</span> {{ incident.IncidentWitness }}
                            </div>

                            <div class="mb-2">
                                <span class="text-muted">Was a PTP Completed for the Work Being Performed?</span>
                                <label class="badge badge-success" v-if="incident.IsPtpCompleted == 1">Yes</label>
                                <label class="badge badge-danger" v-else-if="incident.IsPtpCompleted == 0">No</label>
                                <label class="badge badge-default" v-else>N/A</label>
                            </div>

                            <div class="mb-2">
                                <span class="text-muted">Was the PTP Thorough and Identified All Hazards and Corrective Measures for the Task?</span>
                                <label class="badge badge-success" v-if="incident.IsPtpThorough == 1">Yes</label>
                                <label class="badge badge-danger" v-else-if="incident.IsPtpThorough == 0">No</label>
                                <label class="badge badge-default" v-else>N/A</label>
                            </div>

                            <incident-sections-view :sections="sections" baseSection="incident"></incident-sections-view>
                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header">
                            <strong>
                                <i class="fa fa-building"></i>
                                <template v-if="incident.JobsiteId">
                                    Jobsite
                                </template>
                                <template v-else>
                                    Location
                                </template>
                            </strong>
                        </div>
                        <div class="card-body">
                            <template v-if="incident.JobsiteId">
                                <div class="row mb-2">
                                    <div class="col-md-3 col-12 text-md-right text-muted">Jobsite#:</div>
                                    <div class="col-md-9 col-12"><router-link :to="`/jobsites/view/${incident.JobsiteId}`">{{ incident.JobsiteNumber }}</router-link></div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col-md-3 col-12 text-md-right text-muted">Jobsite Name:</div>
                                    <div class="col-md-9 col-12"><router-link :to="`/jobsites/view/${incident.JobsiteId}`">{{ incident.JobsiteName }}</router-link></div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col-md-3 col-12 text-md-right text-muted">Jobsite Division:</div>
                                    <div class="col-md-9 col-12">{{ incident.JobsiteDivision }}</div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col-md-3 col-12 text-md-right text-muted">Address:</div>
                                    <div class="col-md-9 col-12">
                                        {{ incident.JobsiteAddress }} {{ incident.JobsiteAddress2 }}<br/>
                                        {{ incident.JobsiteCity }}, {{ incident.JobsiteState }} {{ incident.JobsiteZip }}
                                    </div>
                                </div>
                            </template>
                            <template v-else-if="incident.other_location">
                                <div class="row mb-2">
                                    <div class="col-md-3 col-12 text-md-right text-muted">Other Location:</div>
                                    <div class="col-md-9 col-12">{{ incident.other_location }}</div>
                                </div>
                            </template>
                            <template v-else>
                                <div class="row mb-2">
                                    <div class="col-md-3 col-12 text-md-right text-muted">Jobsite:</div>
                                    <div class="col-md-9 col-12">No Jobsite Selected</div>
                                </div>
                            </template>

                            <incident-sections-view :sections="sections" baseSection="jobsite"></incident-sections-view>
                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header"><strong><i class="fa fa-user"></i> Employee</strong></div>

                        <div class="card-body">
                            <div class="row mb-2">
                                <div class="col-md-3 col-12 text-md-right text-muted">Name:</div>
                                <div class="col-md-9 col-12">
                                    <router-link :to="`/employees/view/${incident.EmployeeId}`" v-if="incident.EmployeeId">{{ incident.EmployeeFirstName }} {{ incident.EmployeeLastName }}</router-link>
                                    <span v-else>{{ incident.TempEmployeeName }}</span>
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-md-3 col-12 text-md-right text-muted">Other Employee:</div>
                                <div class="col-md-9 col-12">
                                    <span v-if="incident.IsTempEmployee">Yes</span>
                                    <span v-else>No</span>
                                </div>
                            </div>
                            <div class="row mb-2" v-if="incident.EmployeeTitle">
                                <div class="col-md-3 col-12 text-md-right text-muted">Job Title:</div>
                                <div class="col-md-9 col-12">{{ incident.EmployeeTitle }}</div>
                            </div>
                            <div class="row mb-2" v-if="incident.IsContractor">
                                <div class="col-md-3 col-12 text-md-right text-muted">Subcontractor:</div>
                                <div class="col-md-9 col-12">
                                    <router-link :to="`/subcontractors/view/${incident.subcontractor_id}`" v-if="incident.subcontractor_id">{{ incident.subcontractor_name }}</router-link>
                                    <span v-else>{{ incident.ContractorCompany }}</span>
                                </div>
                            </div>
                            <div class="row mb-2" v-if="incident.EmployeePhone">
                                <div class="col-md-3 col-12 text-md-right text-muted">Phone:</div>
                                <div class="col-md-9 col-12">{{ incident.EmployeePhone }}</div>
                            </div>
                            <div class="row mb-2" v-if="incident.EmployeeAddress || incident.EmployeeCity || incident.EmployeeState">
                                <div class="col-md-3 col-12 text-md-right text-muted">Address:</div>
                                <div class="col-md-9 col-12">
                                    {{ incident.EmployeeAddress }} {{ incident.EmployeeAddress2 }}<br/>
                                    {{ incident.EmployeeCity }}, {{ incident.EmployeeState }} {{ incident.EmployeeZip }}
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-md-3 col-12 text-md-right text-muted">Additional Employees</div>
                                <div class="col-md-9 col-12">
                                    <ul class="list-unstyled">
                                        <li v-for="emp in incident.additional_employees">
                                            <router-link :to="`/employees/view/${emp.EmployeeId}`">
                                                {{ emp.FirstName + ' ' + emp.LastName }}
                                                <template v-if="emp.EmployeeNumber">
                                                    - {{ emp.EmployeeNumber }}
                                                </template>
                                            </router-link>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <incident-sections-view :sections="sections" baseSection="employee"></incident-sections-view>
                        </div>
                    </div>

                    <div class="card mb-3" v-if="incident.inci_require_disc_actions">
                        <div class="card-header"><strong><i class="fa fa-gavel"></i> Disciplinary Action</strong></div>
                        <div class="card-body">
                            <div class="row mb-2">
                                <div class="col-md-4 col-12 text-md-right text-muted">Does this Incident Require Employee Disciplinary Action?</div>
                                <div class="col-md-7 col-12">
                                    <label class="badge badge-warning" v-if="incident.disc_action_required == 1">Yes</label>
                                    <label class="badge badge-secondary" v-else>No</label>
                                </div>
                            </div>
                            <div class="row mb-2" v-if="incident.disc_action_id">
                                <div class="col-md-4 col-12 text-md-right text-muted">Disciplinary Action:</div>
                                <div class="col-md-7 col-12">
                                    <router-link :to="`/employees/disciplinary-actions/view/${disc_action.disc_action_id}`">
                                        <template v-if="disc_action.disc_violation_id">{{ disc_action.violation_name }}</template>
                                        <template v-else>{{ disc_action.other_violation_name }}</template>
                                        on {{ disc_action.disc_action_date | moment("MM/DD/YYYY") }} by {{ disc_action.employee_first_name }} {{ disc_action.employee_last_name }}
                                    </router-link>
                                </div>
                            </div>

                            <incident-sections-view :sections="sections" baseSection="disc_action"></incident-sections-view>
                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header"><strong><i class="fa fa-group"></i> Crew</strong></div>
                        <div class="card-body">
                            <div class="row mb-2">
                                <div class="col-md-3 col-12 text-md-right text-muted">Foreman:</div>
                                <div class="col-md-9 col-12">
                                    {{ incident.foreman_name }}
                                    <span class="text-info" title="Legacy Value" v-if="incident.Foreman">({{ incident.Foreman }})</span>
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-md-3 col-12 text-md-right text-muted">Leadman:</div>
                                <div class="col-md-9 col-12">
                                    {{ incident.leadman_name }}
                                    <span class="text-info" title="Legacy Value" v-if="incident.Leadman">({{ incident.Leadman }})</span>
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-md-3 col-12 text-md-right text-muted">Superintendent:</div>
                                <div class="col-md-9 col-12">
                                    <user-name :user="superintendent" />
                                    <span class="text-info" title="Legacy Value" v-if="incident.Superintendent">({{ incident.Superintendent }})</span>
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-md-3 col-12 text-md-right text-muted">Project Manager:</div>
                                <div class="col-md-9 col-12">
                                    <user-name :user="project_manager" />
                                    <span class="text-info" title="Legacy Value" v-if="incident.ProjectManager">({{ incident.ProjectManager }})</span>
                                </div>
                            </div>
                            <div class="row mb-2" v-if="incident.inci_require_supervisor == 1">
                                <div class="col-md-3 col-12 text-md-right text-muted">Supervisor:</div>
                                <div class="col-md-9 col-12">
                                    <employee-name :employee="supervisor" v-if="incident.supervisor_id" />
                                    <span v-else-if="incident.other_supervisor_name">{{ incident.other_supervisor_name }}</span>
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-md-3 col-12 text-md-right text-muted">Crew Size:</div>
                                <div class="col-md-9 col-12">{{ incident.CrewSize }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-md-3 col-12 text-md-right text-muted">Crew Members:</div>
                                <div class="col-md-9 col-12">
                                    <div v-html="incident.CrewMembers"></div>
                                </div>
                            </div>

                            <incident-sections-view :sections="sections" baseSection="crew_members"></incident-sections-view>
                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header"><strong>Root Cause Analysis</strong></div>
                        <div class="card-body">
                            <div class="row mb-2">
                                <div class="col-md-3 col-12 text-md-right text-muted">Hazards:</div>
                                <div class="col-md-9 col-12">
                                    <div class="badge badge-default mt-1 mr-1" v-for="hazard in incident.hazards">{{ hazard.hazard_name }}</div>
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-md-3 col-12 text-md-right text-muted">Contributing Behaviors:</div>
                                <div class="col-md-9 col-12">
                                    <div class="badge badge-default mt-1 mr-1" v-for="behavior in incident.behaviors">{{ behavior.behavior_name }}</div>
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-md-3 col-12 text-md-right text-muted">Contributing Conditions:</div>
                                <div class="col-md-9 col-12">
                                    <div class="badge badge-default mt-1 mr-1" v-for="condition in incident.conditions">{{ condition.condition_name }}</div>
                                </div>
                            </div>
                            <incident-sections-view :sections="sections" baseSection="root_cause"></incident-sections-view>
                            <hr/>

                            <div class="text-muted">Unsafe Act(s)/Work Practice Contributing to Incident</div>
                            <p v-html="incident.UnsafeActOrPractice"></p>

                            <div class="text-muted mt-3">Unsafe Condition (If Applicable)</div>
                            <p v-html="incident.UnsafeCondition"></p>
                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header">
                            <strong><i class="fa fa-truck"></i> Equipment</strong>
                        </div>
                        <div class="card-body">
                            <template v-if="incident.equipment_id">
                                <div class="row mb-2">
                                    <div class="col-md-3 text-md-right text-muted">Type:</div>
                                    <div class="col-md-9">{{ incident.equipment_type_name }}</div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col-md-3 text-md-right text-muted">Model#:</div>
                                    <div class="col-md-9">{{ incident.model_number }}</div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col-md-3 text-md-right text-muted">Serial#:</div>
                                    <div class="col-md-9">
                                        <router-link :to="`/equipment/view/${incident.equipment_id}`">
                                            {{ incident.serial_number }}
                                        </router-link>
                                    </div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col-md-3 text-md-right text-muted">Equipment Name:</div>
                                    <div class="col-md-9">{{ incident.equipment_name }}</div>
                                </div>
                            </template>
                            <template v-else>
                                <div class="row mb-2">
                                    <div class="col-md-3 text-md-right text-muted">Equipment:</div>
                                    <div class="col-md-9">{{ incident.Equipment }}</div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col-md-3 text-md-right text-muted">Equipment Make:</div>
                                    <div class="col-md-9">{{ incident.EquipmentMake }}</div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col-md-3 text-md-right text-muted">Equipment Model#:</div>
                                    <div class="col-md-9">{{ incident.EquipmentModel }}</div>
                                </div>
                                <div class="row mb-2">
                                    <div class="col-md-3 text-md-right text-muted">Equipment Serial#:</div>
                                    <div class="col-md-9">{{ incident.EquipmentSerial }}</div>
                                </div>
                            </template>

                            <div class="row mb-2">
                                <div class="col-md-3 col-12 text-md-right text-muted">Additional Equipment</div>
                                <div class="col-md-9 col-12">
                                    <ul class="list-unstyled">
                                        <li v-for="equip in incident.additional_equipment">
                                            <router-link :to="`/equipment/view/${equip.equipment_id}`">
                                                {{ equip.equipment_type_name + ': ' + equip.serial_number }}
                                                <template v-if="equip.model_number">
                                                    - {{ equip.model_number }}
                                                </template>
                                            </router-link>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <incident-sections-view :sections="sections" baseSection="equipment"></incident-sections-view>
                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header"><strong>Investigation Participants</strong></div>
                        <div class="card-body">
                            <div class="mb-2" v-html="incident.incident_participants"></div>
                            <hr/>

                            <incident-sections-view :sections="sections" baseSection="participants"></incident-sections-view>
                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header"><strong>Type of Work Being Performed At Time of Incident</strong></div>
                        <div class="card-body">
                            <div class="mb-2" v-html="incident.TypeOfWorkPerformed"></div>
                            <hr/>

                            <incident-sections-view :sections="sections" baseSection="type_of_work"></incident-sections-view>
                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header"><strong>Detailed Description of Incident</strong></div>
                        <div class="card-body">
                            <div class="mb-2" v-html="incident.DetailedDescription"></div>
                            <hr/>

                            <incident-sections-view :sections="sections" baseSection="description"></incident-sections-view>
                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header"><strong>Corrective Actions ({{ actions.length }})</strong></div>
                        <div class="card-body">
                            <corrective-action-table-list :actions="actions" view="cards"></corrective-action-table-list>

                            <div class="mt-4">
                                <incident-sections-view :sections="sections" baseSection="actions"></incident-sections-view>
                            </div>
                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header">
                            <strong><i class="fa fa-heartbeat"></i> Injury/Medical</strong>
                        </div>
                        <div class="card-body">
                            <div class="row mb-2">
                                <div class="col-md-3 text-md-right text-muted">What Object or Substance Directly Harmed Employee?</div>
                                <div class="col-md-9">{{ incident.InjuryObjectOrSubstance }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-md-3 text-md-right text-muted">Medical Professional</div>
                                <div class="col-md-9">{{ incident.MedicalProfessional }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-md-3 text-md-right text-muted">Did Employee Return to Work?</div>
                                <div class="col-md-9">
                                    <template v-if="incident.IsReturnToWork == 1">Yes</template>
                                    <template v-if="incident.IsReturnToWork == 0">No</template>
                                </div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-md-3 text-md-right text-muted">Date Returned to Work</div>
                                <div class="col-md-9">{{ incident.ReturnToWorkDate | moment("MM/DD/YYYY") }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-md-3 text-md-right text-muted">Place of Treatment</div>
                                <div class="col-md-9">{{ incident.FacilityTypeName }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-md-3 text-md-right text-muted">Facility Name</div>
                                <div class="col-md-9">{{ incident.FacilityName }}</div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-md-3 text-md-right text-muted">Facility Address</div>
                                <div class="col-md-9">
                                    {{ incident.FacilityAddress }} {{ incident.FacilityAddress2 }}<br/>
                                    {{ incident.FacilityCity }}<span v-if="incident.FacilityCity && incident.FacilityState">,</span> {{ incident.FacilityState }} {{ incident.FacilityZip }}
                                </div>
                            </div>

                            <div class="mt-2">
                                <incident-sections-view :sections="sections" baseSection="injury"></incident-sections-view>
                            </div>
                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header"><strong>Additional</strong></div>
                        <div class="card-body">
                            <div class="row mb-2">
                                <div class="col-md-3 text-muted">Report Completed By:</div>
                                <div class="col-md-9">{{ incident.user_name }} <span v-if="incident.CreatedByTitle"> | {{ incident.CreatedByTitle }}</span></div>
                            </div>
                            <div class="row mb-2">
                                <div class="col-md-3 text-muted">Report Created Date:</div>
                                <div class="col-md-9">{{ incident.created_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</div>
                            </div>

                            <incident-sections-view :sections="sections" baseSection="additional" labelSize="col-md-3 text-muted" fieldSize="col-md-9"></incident-sections-view>
                            <hr/>

                            <div class="text-muted">Please List Any Additional or Relevant Information:</div>
                            <div v-html="incident.AdditionalInformation"></div>
                        </div>
                    </div>

                    <div class="card mb-3" v-if="this.$root.hasPermission('incidents.manage')">
                        <div class="card-header"><strong>Administrative</strong></div>
                        <div class="card-body">
                            <div class="row">
                                <div class="col-xl-6">
                                    <div class="row mb-2">
                                        <div class="col text-muted">Employee#:</div>
                                        <div class="col-md-7">{{ incident.EmployeeNumber }}</div>
                                    </div>
                                    <div class="row mb-2">
                                        <div class="col text-muted">Employee Date of Birth:</div>
                                        <div class="col-md-7">{{ incident.BirthDate | moment("MM/DD/YYYY") }}</div>
                                    </div>
                                    <div class="row mb-2">
                                        <div class="col text-muted">Employee Date Hired:</div>
                                        <div class="col-md-7">{{ incident.HireDate | moment("MM/DD/YYYY") }}</div>
                                    </div>
                                    <div class="row mb-2">
                                        <div class="col text-muted">Employee Weekly Salary:</div>
                                        <div class="col-md-7">{{ incident.WeeklySalary | currency }}</div>
                                    </div>
                                    <div class="row mb-2">
                                        <div class="col text-muted">Date of Death:</div>
                                        <div class="col-md-7">
                                            <template v-if="incident.DeathDate">
                                                {{ incident.DeathDate | moment("MM/DD/YYYY") }}
                                            </template>
                                        </div>
                                    </div>
                                </div>
                                <div class="col">
                                    <div class="row mb-2">
                                        <div class="col text-muted">OSHA Recordable?:</div>
                                        <div class="col-md-7">
                                            <span v-if="incident.IsOshaRecordable">Yes</span>
                                            <span v-else>No</span>
                                        </div>
                                    </div>
                                    <div class="row mb-2">
                                        <div class="col text-muted">DOT Reportable?:</div>
                                        <div class="col-md-7">
                                            <span v-if="incident.IsDotRecordable">Yes</span>
                                            <span v-else>No</span>
                                        </div>
                                    </div>
                                    <div class="row mb-2">
                                        <div class="col text-muted">OSHA Case#:</div>
                                        <div class="col-md-7">{{ incident.OshaCaseNumber }}</div>
                                    </div>
                                    <div class="row mb-2">
                                        <div class="col text-muted">OSHA Injury Type:</div>
                                        <div class="col-md-7">{{ incident.OshaInjuryTypeName }}</div>
                                    </div>
                                    <div class="row mb-2">
                                        <div class="col text-muted">OSHA Most Serious Outcome:</div>
                                        <div class="col-md-7">{{ incident.OshaOutcomeTypeName }}</div>
                                    </div>
                                    <div class="row mb-2">
                                        <div class="col text-muted">Days Away from Work:</div>
                                        <div class="col-md-7">
                                            <incident-days-away :incident="incident" days_attr="AwayFromWorkDays" start_date_attr="date_first_away_from_work" end_date_attr="date_returned_away_from_work" />
                                        </div>
                                    </div>
                                    <div class="row mb-2">
                                        <div class="col text-muted">Days on Job Transfer Or Restriction:</div>
                                        <div class="col-md-7">
                                            <incident-days-away :incident="incident" days_attr="JobTransferOrRestrictionDays" start_date_attr="date_first_transfer_or_restrict" end_date_attr="date_returned_transfer_or_restrict" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header"><strong><i class="fa fa-files-o"></i> Files &amp; Photos</strong></div>
                        <div class="card-body">
                            <incident-sections-view :sections="sections" baseSection="files"></incident-sections-view>

                            <file-list :files="files" />
                        </div>
                    </div>

                    <incident-sections-view :sections="sections"></incident-sections-view>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment-timezone';

    export default {
        data: function() {
            return {
                userTimezone: this.$store.state.user.timezone,
                loading: true,
                IncidentId: this.$route.params.id,
                incident: {},
                disc_action: {},
                actions: [],
                files: [],
                emailLink: '',
                sections: [],
            }
        },

        computed: {
            superintendent() {
                return {
                    EmployeeId: this.incident.SuperintendentEmployeeId,
                    EmployeeNumber: this.incident.SuperintendentEmployeeNumber,
                    FirstName: this.incident.SuperintendentFirstName,
                    LastName: this.incident.SuperintendentLastName,
                    deleted_at: this.incident.SuperintendentDeletedAt,
                };
            },

            project_manager() {
                return {
                    EmployeeId: this.incident.ProjectManagerEmployeeId,
                    EmployeeNumber: this.incident.ProjectManagerEmployeeNumber,
                    FirstName: this.incident.ProjectManagerFirstName,
                    LastName: this.incident.ProjectManagerLastName,
                    deleted_at: this.incident.ProjectManagerDeletedAt,
                };
            },

            supervisor() {
                return {
                    EmployeeId: this.incident.SupervisorEmployeeId,
                    EmployeeNumber: this.incident.SupervisorEmployeeNumber,
                    FirstName: this.incident.SupervisorFirstName,
                    LastName: this.incident.SupervisorLastName,
                    deleted_at: this.incident.SupervisorDeletedAt,
                };
            },

            incidentDateFormatted(){
                //Format it
                return moment(this.incident.IncidentDate, 'YYYY-MM-DD').format('MM/DD/YYYY');
            },

            incidentName(){
                //Setup
                var name = '';

                //Add the TypeName
                name += (this.incident.IncidentTypeName ? this.incident.IncidentTypeName : 'Incident') + '-';

                //Add the Date
                name += this.incidentDateFormatted + '-';

                //Add the Jobsite
                name += this.incident.JobsiteId ? this.incident.JobsiteName : this.incident.other_location;

                //Send it
                return name;
            }
        },

        methods: {

            incidentPartsUpdated(data){
                //Update the cost
                this.incident.cost = data.incident_cost;

                //Redraw the table
                this.$refs.incidentParts.refreshData();
            },

            getIncident() {
                //Get the data
                this.$http
                    .get('incident/view/' + this.IncidentId)
                    .then(response => {
                        //Set all the data
                        this.incident = response.data.incident;
                        this.actions = response.data.actions;
                        this.disc_action = response.data.disc_action;
                        this.files = response.data.files;
                        this.emailLink = response.data.emailLink;
                        this.sections = response.data.sections;

                        //Set loading
                        this.loading = false;
                    })
                    .catch(response => {

                    });
            },

        },
        mounted() {
            //Get the incidents
            this.getIncident();
        }
    }
</script>
